import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import { AddYearLevelDialogComponent } from '../../../shared/components/dialog/add-year-level/add-year-level.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';

interface YearLevel {
  id: string;
  yearLevel: string;
  year_level_name: string;
  yearlevelid: string;
}

interface APIResponse {
  status: boolean;
  data: any;
  message?: string;
}

@Component({
  selector: 'app-yearlevel',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, TableComponent],
  templateUrl: './yearlevel.component.html',
  styleUrls: ['./yearlevel.component.scss'],
})
export class YearLevelComponent implements OnInit {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;

  columns: string[] = ['yearLevel'];
  yearLevels: any[] = [];

  loading: boolean = false;
  private dialogRef: any;

  constructor(
    public router: Router,
    private dialog: MatDialog,
    private httpService: HttpService,
    private http: HttpClient,
    private location: Location,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadYearLevels();
  }

  goBack(): void {
    this.location.back();
  }

  openAddYearLevel(yearLevelData?: any): void {
    this.dialogRef = this.dialog.open(AddYearLevelDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: yearLevelData || null,
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    this.dialogRef
      .afterClosed()
      .subscribe((result: { success: any; data: any }) => {
        if (result?.success) {
          this.loadYearLevels();
        }
      });
  }

  onDelete(yearLevel: YearLevel): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${yearLevel.yearLevel}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.deleteYearLevel(yearLevel);
        }
      });
  }

  private deleteYearLevel(yearLevel: YearLevel): void {
    this.loading = true;
    this.httpService
      .delete<APIResponse>(`settings/yearlevel/${yearLevel.id}`)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.yearLevels = this.yearLevels.filter(
              (y) => y.id !== yearLevel.id,
            );
            this.adjustPaginationAfterDelete();
            this.showToast(
              response.message || 'Year level deleted successfully!',
              'success',
            );
          } else {
            this.showToast(
              response?.message || 'Failed to delete year level',
              'error',
            );
          }
        },
        error: (err) => {
          console.error('Delete error:', err);
          this.showToast(
            err?.message || 'Failed to delete year level',
            'error',
          );
        },
      });
  }

  onEdit(yearLevel: YearLevel): void {
    this.openAddYearLevel(yearLevel);
  }

  private loadYearLevels(): void {
    this.loading = true;
    this.httpService
      .get('settings/yearlevel')
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          if (response?.status && response?.data) {
            this.yearLevels = this.transformApiData(response.data);
          }
        },
        error: (err) => {
          console.error('Failed to load year levels:', err);
          this.showToast(err?.message || 'Failed to load year levels', 'error');
        },
      });
  }

  private transformApiData(apiData: any[]) {
    return apiData.map((item) => ({
      id: item.yearlevelid,
      yearLevel: item.year_level_name,
    }));
  }

  private adjustPaginationAfterDelete(): void {
    const totalPages = Math.ceil(
      this.yearLevels.length / (this.tableComponent?.pageSize || 10),
    );
    if (
      this.tableComponent &&
      this.tableComponent.currentPage > totalPages &&
      totalPages > 0
    ) {
      this.tableComponent.currentPage = totalPages;
    }
  }

  private getErrorMessage(err: any): string {
    if (err.error?.message) return err.error.message;
    if (err.message) return err.message;
    if (err.status === 415)
      return 'Unsupported media type. Please check file format.';
    if (err.status === 413) return 'File too large. Maximum size is 5MB.';
    return 'An unexpected error occurred. Please try again.';
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    if (!message) return;

    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: [`${type}-toast`],
    });
  }
}
